import { SelectedFileStatus } from '@vizit/atoms';
import type { UploadQueueItemData } from './upload-queue.context';

export type UploadQueueAction =
  | { type: 'ADD_FILES'; payload: UploadQueueItemData[] }
  | { type: 'REMOVE_ITEM'; payload: { id: string } }
  | { type: 'UPDATE_PROGRESS'; payload: { id: string; progress: number } }
  | { type: 'UPDATE_STATUS'; payload: { id: string; status: SelectedFileStatus; error?: string } }
  | { type: 'SET_UPLOADING'; payload: { ids: string[] } }
  | { type: 'CLEAR_COMPLETED' }
  | { type: 'CLEAR_UPLOADED' };

/** The most files the queue panel lists. Files still to upload are capped apart, at `MAX_FILES_PER_UPLOAD`. */
export const MAX_LISTED_FILES = 50;

export interface UploadQueueState {
  items: UploadQueueItemData[];
  /** Finished files of the running batch dropped from the list to keep it under `MAX_LISTED_FILES`, still counted. */
  clearedCount: number;
  /** How many of `clearedCount` failed to upload. */
  clearedFailedCount: number;
}

const isLeftToUpload = (item: UploadQueueItemData) =>
  item.status === SelectedFileStatus.Ready || item.status === SelectedFileStatus.Uploading;

/** Drops the oldest finished files past `MAX_LISTED_FILES`, uploaded ones before failed ones. */
function withinMaxListedFiles(items: UploadQueueItemData[]): UploadQueueItemData[] {
  const excess = items.length - MAX_LISTED_FILES;
  if (excess <= 0) {
    return items;
  }
  const dropped = new Set<string>();
  for (const status of [SelectedFileStatus.Success, SelectedFileStatus.Error]) {
    for (const item of items) {
      if (dropped.size < excess && item.status === status) {
        dropped.add(item.id);
      }
    }
  }
  return items.filter((item) => !dropped.has(item.id));
}

/**
 * The rows the queue panel shows, at most `MAX_LISTED_FILES`. Past that it leaves out the oldest uploaded files, then
 * the newest files still waiting, then the oldest failed ones, so the files being uploaded stay in view.
 */
export function pickListedItems(items: UploadQueueItemData[]): UploadQueueItemData[] {
  const excess = items.length - MAX_LISTED_FILES;
  if (excess <= 0) {
    return items;
  }
  const leftOut = new Set<string>();
  const leaveOut = (candidates: UploadQueueItemData[]) => {
    for (const item of candidates) {
      if (leftOut.size >= excess) {
        return;
      }
      leftOut.add(item.id);
    }
  };
  leaveOut(items.filter((item) => item.status === SelectedFileStatus.Success));
  leaveOut(items.filter((item) => item.status === SelectedFileStatus.Ready).reverse());
  leaveOut(items.filter((item) => item.status === SelectedFileStatus.Error));
  return items.filter((item) => !leftOut.has(item.id));
}

/**
 * Reducer for managing upload queue state
 *
 * @param state - Current state
 * @param action - Action to perform
 * @returns New state
 */
export function uploadQueueReducer(state: UploadQueueState, action: UploadQueueAction): UploadQueueState {
  switch (action.type) {
    case 'ADD_FILES': {
      // A batch runs until nothing is left to upload; the next one starts without the last one's files.
      const isNewBatch = !state.items.some(isLeftToUpload);
      const added = [...(isNewBatch ? [] : state.items), ...action.payload];
      const items = withinMaxListedFiles(added);
      const failedCount = (list: UploadQueueItemData[]) =>
        list.filter((item) => item.status === SelectedFileStatus.Error).length;
      return {
        ...state,
        items,
        clearedCount: (isNewBatch ? 0 : state.clearedCount) + added.length - items.length,
        clearedFailedCount: (isNewBatch ? 0 : state.clearedFailedCount) + failedCount(added) - failedCount(items),
      };
    }

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload.id),
      };

    case 'UPDATE_PROGRESS':
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id ? { ...item, progress: action.payload.progress } : item,
        ),
      };

    case 'UPDATE_STATUS':
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, status: action.payload.status, error: action.payload.error }
            : item,
        ),
      };

    case 'SET_UPLOADING':
      return {
        ...state,
        items: state.items.map((item) =>
          action.payload.ids.includes(item.id) ? { ...item, status: SelectedFileStatus.Uploading } : item,
        ),
      };

    case 'CLEAR_COMPLETED':
      return {
        ...state,
        clearedCount: 0,
        clearedFailedCount: 0,
        items: state.items.filter(
          (item) => item.status !== SelectedFileStatus.Success && item.status !== SelectedFileStatus.Error,
        ),
      };

    case 'CLEAR_UPLOADED':
      // Uploaded files leave, dropped ones included; the failed ones dropped earlier stay counted.
      return {
        ...state,
        clearedCount: state.clearedFailedCount,
        items: state.items.filter((item) => item.status !== SelectedFileStatus.Success),
      };

    default:
      return state;
  }
}
